import {
	InvalidFormatError,
	InvalidLengthError,
	InvalidRangeError,
} from "../errors/ValidationError.js";
import { isValidPrivateKey } from "../../crypto/Secp256k1/isValidPrivateKey.js";
import type { PrivateKeyType } from "./PrivateKeyType.js";

const HEX_REGEX = /^[0-9a-fA-F]+$/;

/**
 * Create PrivateKey from hex string
 *
 * @param hex - Hex string (32 bytes)
 * @returns Private key
 * @throws {InvalidFormatError} If hex string format is invalid
 * @throws {InvalidLengthError} If hex is not 32 bytes
 * @throws {InvalidRangeError} If private key is out of range [1, n-1]
 *
 * @example
 * ```typescript
 * const pk = PrivateKey.from("0x1234...");
 * ```
 */
export function from(hex: string): PrivateKeyType {
	const hexStr = hex.startsWith("0x") ? hex.slice(2) : hex;
	if (!HEX_REGEX.test(hexStr)) {
		throw new InvalidFormatError("Invalid hex string", {
			value: hex,
			expected: "Valid hex string",
			code: -32602,
			docsPath: "/primitives/private-key/from#error-handling",
		});
	}
	if (hexStr.length !== 64) {
		throw new InvalidLengthError(
			`Private key must be 32 bytes (64 hex chars), got ${hexStr.length}`,
			{
				value: hexStr.length,
				expected: "64 hex characters (32 bytes)",
				code: -32602,
				docsPath: "/primitives/private-key/from#error-handling",
			},
		);
	}
	const bytes = new Uint8Array(32);
	for (let i = 0; i < 32; i++) {
		bytes[i] = Number.parseInt(hexStr.slice(i * 2, i * 2 + 2), 16);
	}
	if (!isValidPrivateKey(bytes)) {
		throw new InvalidRangeError("Private key must be in range [1, n-1]", {
			value: hex,
			expected: "Private key in range [1, n-1]",
			code: -32602,
			docsPath: "/primitives/private-key/from#error-handling",
		});
	}
	return bytes as PrivateKeyType;
}
